import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Search, Filter, RefreshCw, BadgeCheck, Crown, Sparkles, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import NativeSelect from "@/components/forms/NativeSelect";
import api from "@/lib/api";

/**
 * AdminOwners — searchable/filterable directory of every spa owner + their
 * linked spa, with a one-click CSV export.
 *
 * Data source: GET /api/admin/owners (JSON) and /api/admin/owners/export (CSV).
 * Both accept the same query params (status_filter, plan_filter, q) so what
 * the admin sees on-screen is what they get in the download.
 *
 * The CSV download uses a Blob URL rather than pointing an `<a>` at the API
 * endpoint directly — this keeps the request authenticated via our axios
 * client (which attaches the `psg_token` cookie) rather than relying on
 * browser-driven cookie propagation, which is sometimes flaky in embedded
 * previews.
 */

const PLAN_META = {
  free:     { label: "Free",     icon: Circle,   chip: "bg-[#F5EFE6] text-[#4B5563] border-[#E7DDCF]" },
  featured: { label: "Featured", icon: Sparkles, chip: "bg-[#EEF2FF] text-[#1E3A8A] border-[#93C5FD]" },
  premium:  { label: "Premium",  icon: Crown,    chip: "bg-[#F5EFE6] text-[#7A5A22] border-[#C9A86A]" },
};

export default function AdminOwners() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (planFilter) params.plan_filter = planFilter;
      if (statusFilter) params.status_filter = statusFilter;
      const { data } = await api.get("/admin/owners", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load owners");
    } finally {
      setLoading(false);
    }
  }, [q, planFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (planFilter) params.plan_filter = planFilter;
      if (statusFilter) params.status_filter = statusFilter;
      const resp = await api.get("/admin/owners/export", {
        params,
        responseType: "blob",
      });
      // Extract server-side filename hint or fall back to a client-generated one.
      let filename = `psg-owners-${new Date().toISOString().slice(0, 10)}.csv`;
      const disp = resp.headers?.["content-disposition"];
      if (disp) {
        const m = /filename="?([^";]+)"?/.exec(disp);
        if (m) filename = m[1];
      }
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const rowCount = resp.headers?.["x-row-count"];
      toast.success(rowCount ? `Exported ${rowCount} rows to ${filename}` : `Exported to ${filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-owners-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Directory</div>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#111827] mt-1">Spa Owners</h1>
          <p className="text-sm text-[#4B5563] mt-1 max-w-2xl">
            Every registered spa owner with their linked listing, contact details, and current plan.
            Filter and search live, then export to CSV for offline use.
          </p>
        </div>
        <Button
          onClick={exportCsv}
          disabled={exporting || loading}
          className="gap-2 bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white"
          data-testid="admin-owners-export-button"
        >
          <Download size={14} /> {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      {/* Filter bar */}
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs flex items-center gap-1"><Search size={12} /> Search</Label>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, spa…"
            className="mt-1"
            data-testid="admin-owners-search"
          />
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs flex items-center gap-1"><Filter size={12} /> Plan</Label>
          <NativeSelect
            value={planFilter}
            onChange={(v) => setPlanFilter(v)}
            className="mt-1"
            data-testid="admin-owners-filter-plan"
          >
            <option value="">All Plans</option>
            <option value="free">Free</option>
            <option value="featured">Featured</option>
            <option value="premium">Premium</option>
          </NativeSelect>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs flex items-center gap-1"><Filter size={12} /> Status</Label>
          <NativeSelect
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            className="mt-1"
            data-testid="admin-owners-filter-status"
          >
            <option value="">All Users</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </NativeSelect>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="admin-owners-refresh">
          <RefreshCw size={13} /> Refresh
        </Button>
      </div>

      {/* Count */}
      <div className="text-sm text-[#4B5563]">
        Showing <strong className="text-[#111827]">{items.length}</strong> owner{items.length === 1 ? "" : "s"}.
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border border-[#E7DDCF] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-[#4B5563]">Loading owners…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#4B5563]">No owners match the current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-[#7A5A22] bg-[#FBF7F1] border-b border-[#E7DDCF]">
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Spa</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Verified</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const plan = row.spa?.plan || "free";
                  const pm = PLAN_META[plan] || PLAN_META.free;
                  const PIcon = pm.icon;
                  const joined = row.created_at ? new Date(row.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
                  return (
                    <tr key={row.owner_id} className="border-b border-[#F5EFE6] hover:bg-[#FBF7F0]" data-testid={`owner-row-${row.owner_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#111827] truncate max-w-[200px]">{row.owner_name || "—"}</div>
                        <div className="text-[11px] text-[#7A5A22]">id: {row.owner_id?.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-[#111827] truncate max-w-[220px]" title={row.email}>{row.email || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-[#111827]">{row.phone || "—"}</td>
                      <td className="px-4 py-3">
                        {row.spa ? (
                          <div>
                            <div className="text-[#111827] truncate max-w-[200px]" title={row.spa.spa_name}>{row.spa.spa_name}</div>
                            <div className="text-[11px] text-[#7A5A22]">/{row.spa.slug}</div>
                          </div>
                        ) : <span className="text-xs text-[#4B5563] italic">No listing</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${pm.chip}`}>
                          <PIcon size={11} /> {pm.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5 text-[11px]">
                          <span className={row.email_verified ? "text-[#065F46]" : "text-[#4B5563]"}>
                            {row.email_verified ? <BadgeCheck size={11} className="inline mr-0.5" /> : null}
                            Email {row.email_verified ? "✓" : "✗"}
                          </span>
                          <span className={row.phone_verified ? "text-[#065F46]" : "text-[#4B5563]"}>
                            {row.phone_verified ? <BadgeCheck size={11} className="inline mr-0.5" /> : null}
                            Phone {row.phone_verified ? "✓" : "✗"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#111827]">{joined}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${row.is_active ? "bg-[#DCFCE7] text-[#065F46] border-[#86EFAC]" : "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]"}`}>
                          {row.is_active ? "Active" : "Archived"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
